import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import config from "../../config";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  FormControl,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import InterestDetailsModal from "modals/InterestDetailsModal";

function getStatusColor(status) {
  switch (status) {
    case "new":
        return "info";
    case "viewed":
      return "info";
    case "contacted":
      return "warning";
    case "closed":
      return "success";
    default:
      return "default";
  }
}

function getStatusLabel(status, t) {
  switch (status) {
    case "new":
      return t("status.new");
    case "viewed":
      return t("status.viewed");
    case "contacted":
      return t("status.contacted");
    case "closed":
      return t("status.closed");
    default:
      return "-";
  }
}

function formatDate(date) {
  if (!date) return "-";
  return new Date(date).toLocaleString("el-GR");
}

function getFullName(user) {
  if (!user) return "-";
  const fullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return fullName || "-";
}

const gridColumns = "1.2fr 1.2fr 1.4fr 1fr 1.2fr 1fr";

const cellSx = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "#fff",
  fontSize: "1rem",
};

export default function AdminInterestsPage() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const limit = 20;
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [statusFilter, setStatusFilter] = useState("");

  const [selectedInterest, setSelectedInterest] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const token = useMemo(() => localStorage.getItem("admin_jwt"), []);

  const fetchInterests = async (targetPage = 1, targetStatus = statusFilter) => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: String(targetPage),
        limit: String(limit),
      });

      if (targetStatus) {
        params.append("status", targetStatus);
      }

      const res = await axios.get(`${config.BACKEND_BASE_URL}/api/admin/interests?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const payload = res.data || {};
      setRows(payload.data || []);
      setTotalPages(payload.pagination?.totalPages || 1);
      setTotal(payload.pagination?.total || 0);
    } catch (error) {
      console.error("Σφάλμα κατά την ανάκτηση ενδιαφερόντων:", error);
      setRows([]);
      setTotalPages(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterests(page, statusFilter);
  }, [page, statusFilter]);

  const handleRowClick = (row) => {
    setSelectedInterest(row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedInterest(null);
  };

  const handleStatusUpdated = (updatedInterest) => {
    if (statusFilter && updatedInterest.status !== statusFilter) {
      fetchInterests(page, statusFilter);
      setSelectedInterest((prev) =>
        prev ? { ...prev, status: updatedInterest.status } : prev
      );
      return;
    }

    setRows((prev) =>
      prev.map((item) =>
        item._id === updatedInterest._id
          ? { ...item, status: updatedInterest.status }
          : item
      )
    );

    setSelectedInterest((prev) =>
      prev ? { ...prev, status: updatedInterest.status } : prev
    );
  };

  const handleFilterChange = (e) => {
    setPage(1);
    setStatusFilter(e.target.value);
  };

  return (
    <DashboardLayout>
      <Box py={3} sx={{ color: "#fff" }}>
        <Card
          sx={{
            p: 3,
            color: "#fff",
            borderRadius: 3,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
            mb={3}
          >
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ color: "#fff" }}>
                {t("interests.title")}
              </Typography>
              <Typography variant="body2" sx={{ color: "#fff", opacity: 0.8 }}>
                {t("interests.totalRecords", { total })}
              </Typography>
            </Box>

            <Box sx={{ minWidth: { xs: "100%", md: 260 } }}>
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.75)", mb: 0.75 }}
              >
                {t("interests.statusFilter")}
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={statusFilter}
                  onChange={handleFilterChange}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return t("status.all");
                    return getStatusLabel(selected, t);
                  }}
                  sx={{
                    color: "#fff",
                    bgcolor: "rgba(255,255,255,0.04)",
                    borderRadius: 2,
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.2)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.35)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.45)",
                    },
                    ".MuiSelect-icon": {
                      color: "#fff",
                    },
                    px: 1,
                    py: 0.25,
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: "#23233a",
                        color: "#fff",
                        border: "1px solid rgba(255,255,255,0.1)",
                        "& .MuiMenuItem-root": {
                          fontSize: "0.95rem",
                        },
                        "& .MuiMenuItem-root.Mui-selected": {
                          bgcolor: "rgba(255,255,255,0.08)",
                        },
                        "& .MuiMenuItem-root:hover": {
                          bgcolor: "rgba(255,255,255,0.06)",
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="">{t("status.all")}</MenuItem>
                  <MenuItem value="new">{t("status.new")}</MenuItem>
                  <MenuItem value="viewed">{t("status.viewed")}</MenuItem>
                  <MenuItem value="contacted">{t("status.contacted")}</MenuItem>
                  <MenuItem value="closed">{t("status.closed")}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box py={6} textAlign="center">
              <Typography variant="body1" sx={{ color: "#fff", opacity: 0.8 }}>
                {t("interests.noInterests")}
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: gridColumns,
                    columnGap: 2,
                    px: 2,
                    py: 1.75,
                    borderBottom: "1px solid rgba(255,255,255,0.2)",
                    width: "100%",
                    minWidth: 1000,
                  }}
                >
                  <Typography fontWeight={700} sx={{ ...cellSx, fontSize: "1.25rem", opacity: 0.9 }}>
                    {t("interests.colTitle")}
                  </Typography>
                  <Typography fontWeight={700} sx={{ ...cellSx, fontSize: "1.25rem", opacity: 0.9 }}>
                    {t("interests.colName")}
                  </Typography>
                  <Typography fontWeight={700} sx={{ ...cellSx, fontSize: "1.25rem", opacity: 0.9 }}>
                    {t("interests.colEmail")}
                  </Typography>
                  <Typography fontWeight={700} sx={{ ...cellSx, fontSize: "1.25rem", opacity: 0.9 }}>
                    {t("interests.colPhone")}
                  </Typography>
                  <Typography fontWeight={700} sx={{ ...cellSx, fontSize: "1.25rem", opacity: 0.9 }}>
                    {t("interests.colDate")}
                  </Typography>
                  <Typography
                    fontWeight={700}
                    sx={{ ...cellSx, textAlign: "center", fontSize: "1.25rem", opacity: 0.9 }}
                  >
                    {t("interests.colStatus")}
                  </Typography>
                </Box>

                <Box>
                  {rows.map((row) => (
                    <Box
                      key={row._id}
                      onClick={() => handleRowClick(row)}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: gridColumns,
                        columnGap: 2,
                        px: 2,
                        py: 1.75,
                        width: "100%",
                        minWidth: 1000,
                        borderBottom: "1px solid rgba(255,255,255,0.12)",
                        cursor: "pointer",
                        transition: "opacity 0.2s ease, background-color 0.2s ease",
                        alignItems: "center",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.05)",
                          opacity: 0.8,
                        },
                      }}
                    >
                      <Typography sx={cellSx}>{row.title || "-"}</Typography>
                      <Typography sx={cellSx}>{getFullName(row.userId)}</Typography>
                      <Typography sx={cellSx}>
                        {row.userEmail || row.userId?.email || "-"}
                      </Typography>
                      <Typography sx={cellSx}>{row.telephone || "-"}</Typography>
                      <Typography sx={cellSx}>
                        {formatDate(row.createdAt)}
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "center", minWidth: 0 }}>
                        <Chip
                          label={getStatusLabel(row.status, t)}
                          color={getStatusColor(row.status)}
                          size="small"
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box display="flex" justifyContent="center" mt={3}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, value) => setPage(value)}
                  color="primary"
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#fff",
                    },
                    "& .MuiPaginationItem-icon": {
                      color: "#fff",
                    },
                    "& .Mui-selected": {
                      color: "#fff",
                    },
                    "& .MuiPaginationItem-previousNext": {
                      color: "#fff",
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Card>
      </Box>

      <InterestDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        interest={selectedInterest}
        token={token}
        onStatusUpdated={handleStatusUpdated}
      />
    </DashboardLayout>
  );
}